import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  CheckCircle,
  Leaf,
  Thermometer,
  Sparkles,
  Zap,
  Shield,
  Award,
  Download,
  MapPin,
  Volume2,
  Battery,
  Maximize2,
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { getBrand, getCategory, getProductsByBrand } from '../../data/products';

const pillarIcons = {
  Leaf,
  Thermometer,
  Sparkles,
  Zap,
  Shield,
  Award,
  MapPin,
  Volume2,
  Battery,
  Maximize2,
};

/**
 * Generic Brand page — driven entirely by data in /src/data/products.js.
 * Every new brand should add an entry to the `brands` object there;
 * to expose it as a route, create a thin wrapper file that renders
 * <BrandPage brandSlug="{slug}" />.
 */
const BrandPage = ({ brandSlug }) => {
  const brand = getBrand(brandSlug);
  const items = getProductsByBrand(brandSlug);

  if (!brand) {
    return <Navigate to="/brands" replace />;
  }

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://supaenergyhub.com.au/' },
        { '@type': 'ListItem', position: 2, name: 'Brands', item: 'https://supaenergyhub.com.au/brands' },
        { '@type': 'ListItem', position: 3, name: brand.name, item: `https://supaenergyhub.com.au/brands/${brand.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Brand',
      name: brand.name,
      logo: `https://supaenergyhub.com.au${brand.logo}`,
      description: brand.shortDescription,
      foundingDate: brand.established,
    },
  ];

  const seriesLabel = brand.seriesName || '';
  const pills = brand.modelCardPills || [];
  const category = getCategory(brand.category);
  const categoryName = category?.name || 'Products';
  const categorySlug = brand.category;
  const categoryLandingPath = category?.landingPath || `/products/${categorySlug}`;

  return (
    <div className="min-h-screen">
      <SEO
        title={brand.seo?.title || `${brand.name} — Supa Energy Hub`}
        description={brand.seo?.description || brand.shortDescription}
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a2820] via-[#2a4032] to-[#3D5A47] relative overflow-hidden text-white">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#B5D43B]/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" data-testid="brand-breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/brands" className="hover:text-white">Brands</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white font-medium">{brand.name}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-block text-[10px] text-[#B5D43B] font-bold tracking-[0.3em] uppercase">
                    EST. {brand.established} · {brand.heroEyebrow}
                  </span>
                </div>
                <h1 className="heading-1 mb-3 leading-tight" data-testid="brand-name">
                  <span className="text-white">{brand.name}</span>{' '}
                  {seriesLabel && <span className="text-[#B5D43B]">{seriesLabel}</span>}
                </h1>
                <p className="text-xl text-white/90 font-medium mb-4">
                  {brand.tagline}
                </p>
                <p className="text-white/70 leading-relaxed mb-8">
                  {brand.shortDescription}
                </p>

                <div className="flex flex-wrap gap-3">
                  <a href="#models">
                    <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 font-semibold" data-testid="brand-view-models-cta">
                      View All Models <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                  {brand.brochureUrl && (
                    <a href={brand.brochureUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="gap-2 border-white text-white hover:bg-white hover:text-[#3D5A47]">
                        <Download className="w-4 h-4" /> Download Brochure
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-8 bg-[#B5D43B]/20 rounded-full blur-3xl" />
                  {brand.productRender && (
                    <img
                      src={brand.productRender}
                      alt={`${brand.name} ${seriesLabel}`}
                      className="relative h-96 w-auto object-contain"
                      style={{ mixBlendMode: 'multiply' }}
                      data-testid="brand-product-render"
                    />
                  )}
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl px-4 py-3 shadow-2xl border-2 border-[#B5D43B]/30">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="h-7 w-auto"
                      data-testid="brand-logo"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Six Pillars */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="heading-2 text-[#3D5A47] mb-3">Engineered around six pillars</h2>
              <p className="text-gray-600">{brand.pillarsIntro}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {brand.pillars.map((p, i) => {
                const Icon = pillarIcons[p.icon] || Award;
                return (
                  <div
                    key={i}
                    className="bg-gray-50 hover:bg-white hover:shadow-md hover:border-[#B5D43B]/50 border border-transparent rounded-2xl p-5 text-center transition-all"
                    data-testid={`brand-pillar-${i}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-[#3D5A47]" />
                    </div>
                    <p className="font-semibold text-[#3D5A47] text-sm">{p.name}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Available Models */}
        <section id="models" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                  The {seriesLabel} Range
                </span>
                <h2 className="heading-2 text-[#3D5A47]">Available {brand.name} Models</h2>
                <p className="text-gray-600 mt-1">
                  Choose the right model for your household — every unit shares the same core {seriesLabel} technology.
                </p>
              </div>
              <Link to={`/products/${categorySlug}`} className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> All {categoryName}
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${categorySlug}/${p.slug}`}
                  className="group relative bg-white rounded-2xl border border-gray-200 hover:border-[#B5D43B]/60 hover:shadow-xl transition-all overflow-hidden flex"
                  data-testid={`brand-model-${p.slug}`}
                >
                  <div className={`w-44 sm:w-52 flex-shrink-0 relative flex items-center justify-center p-3 ${p.imageBlendMode === 'normal' ? 'bg-gradient-to-br from-[#f6f8f3] via-white to-[#e9efe2]' : 'bg-gradient-to-br from-[#1a2820] via-[#3D5A47] to-[#2a4032]'}`}>
                    <div className="absolute top-3 left-3 z-10 bg-[#B5D43B] text-[#1a2820] rounded-lg px-2 py-1.5 text-center shadow-md">
                      <span className="block text-[7px] uppercase tracking-widest font-bold leading-none">{(p.headlineSpec?.label || 'Spec').split(' ')[0]}</span>
                      <div className="flex items-baseline justify-center leading-none mt-0.5">
                        <span className="text-xl font-black">{p.headlineSpec.value}</span>
                        <span className="text-xs font-black">{p.headlineSpec.unit}</span>
                      </div>
                    </div>
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.name}
                        className="h-40 w-auto object-contain"
                        style={p.imageBlendMode === 'normal' ? {} : { mixBlendMode: 'multiply' }}
                        loading="lazy"
                      />
                    )}
                    <span className={`absolute bottom-2 left-0 right-0 text-center text-[8px] uppercase tracking-widest font-mono ${p.imageBlendMode === 'normal' ? 'text-[#3D5A47]/30' : 'text-white/40'}`}>
                      {p.series}
                    </span>
                  </div>

                  <div className="p-5 flex flex-col flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-[#3D5A47] group-hover:text-[#B5D43B] transition-colors leading-tight">
                      {p.name}
                    </h3>
                    <p className="text-xs text-gray-400 mb-2 font-mono">Model {p.model}</p>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 flex-1">
                      {p.tagline}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex gap-1.5">
                        {pills.map((label, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center text-[10px] font-medium text-[#3D5A47] bg-[#B5D43B]/15 px-2 py-1 rounded-full"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#3D5A47] group-hover:text-[#B5D43B]">
                        View <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Installation Gallery */}
        {brand.installationImages && brand.installationImages.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-2xl mx-auto mb-10">
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                  Real-World Installations
                </span>
                <h2 className="heading-2 text-[#3D5A47]">
                  {brand.installationGalleryTitle || `${brand.name} in the wild`}
                </h2>
                <p className="text-gray-600 mt-2">{brand.installationGalleryIntro}</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {brand.installationImages.map((src, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-2xl overflow-hidden bg-gray-200 group shadow-sm"
                    data-testid={`brand-install-${i}`}
                  >
                    <img
                      src={src}
                      alt={`${brand.name} installation ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Why this brand */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-2 mb-6 text-[#3D5A47]">Why we chose {brand.name}</h2>
                {brand.whyParagraphs?.map((p, idx) => (
                  <p key={idx} className="text-gray-600 leading-relaxed mb-4">{p}</p>
                ))}
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-[#3D5A47] mb-4">Standout highlights</h3>
                {brand.highlights?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3.5 bg-gray-50 rounded-xl"
                    data-testid={`brand-highlight-${index}`}
                  >
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Ready for a {brand.name} install?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Our team will help you choose the right {seriesLabel || brand.name} model based on your household size, climate zone, and rebates available in your area.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/get-estimate">
                <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 font-semibold" data-testid="brand-cta-estimate">
                  Get Free Estimate <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to={categoryLandingPath}>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#3D5A47] gap-2">
                  Learn About {categoryName}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-[11px] text-gray-500 leading-relaxed">
              *Warranty terms are set by the manufacturer ({brand.name}) and subject to product registration and conditions of use. Specifications, COP and rebate eligibility are accurate at time of publication and may change without notice. Supa Energy Hub is not affiliated with {brand.name}; all trademarks are the property of their respective owners. Rebate amounts and eligibility vary by program and household circumstances.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BrandPage;
